import { token } from '../theme/tokens.js';

/**
 * A glyph for CON-21's five-value agency taxonomy — Law Enforcement,
 * Prosecution, Defense, Court, Clerks of Court — shown beside an agency's name
 * the way `apps/web`'s `FolderIcon` sits beside a folder's.
 *
 * **Promoted here rather than hand-copied per call site**, unlike `FolderIcon`
 * (three near-identical copies across `apps/web`) — five glyphs times two apps
 * (`apps/web`'s agency lists, `apps/admin`'s fleet table) is past the point
 * duplication stays idiomatic, and a shared component is where the mapping
 * from taxonomy value to glyph lives exactly once.
 *
 * Every path is Lucide, hand-copied like every other icon in this workspace
 * (no `lucide-react` dependency, ADR-0053's reasoning). The house SVG contract
 * — `viewBox 0 0 24 24`, `fill="none"`, `stroke="currentColor"`, 2px round
 * caps/joins — is `FolderIcon`'s, unchanged.
 */

export type AgencyType = 'LawEnforcement' | 'Prosecution' | 'Defense' | 'Court' | 'ClerkOfCourt';

export interface AgencyTypeIconProps {
  readonly type: AgencyType;
  /**
   * The accessible name. **Required, not optional** — unlike `FolderIcon`,
   * which sits beside a name that already says which folder, the type is
   * information the row states nowhere else. This package hardcodes no
   * English (see `packages/ui/CLAUDE.md`), so the label is always the
   * caller's.
   */
  readonly label: string;
  readonly className?: string;
}

function ShieldGlyph() {
  return (
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1Z" />
  );
}

function ScaleGlyph() {
  return (
    <>
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="M7 21h10" />
      <path d="M12 3v18" />
      <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
    </>
  );
}

function BriefcaseGlyph() {
  return (
    <>
      <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      <rect width="20" height="14" x="2" y="6" rx="2" />
    </>
  );
}

function LandmarkGlyph() {
  return (
    <>
      <path d="M10 18v-7" />
      <path d="M14 18v-7" />
      <path d="M18 18v-7" />
      <path d="M3 22h18" />
      <path d="M6 18v-7" />
      <path d="M12 2 3 7v2h18V7Z" />
    </>
  );
}

function StampGlyph() {
  return (
    <>
      <path d="M5 22h14" />
      <path d="M19 15V6.5a1 1 0 0 0-.5-.87l-2.86-1.64a1 1 0 0 0-1.03 0l-2.4 1.37a1 1 0 0 1-1.03 0l-2.4-1.37a1 1 0 0 0-1.03 0L5 5.63a1 1 0 0 0-.5.87V15" />
      <path d="M9 22c-.5-.5-1-1.5-1-3s.5-2.5 1-3c.5.5 1 1.5 1 3s-.5 2.5-1 3" />
      <path d="M14 22c-.5-.5-1-1.5-1-3s.5-2.5 1-3c.5.5 1 1.5 1 3s-.5 2.5-1 3" />
      <path d="M3 15h18" />
    </>
  );
}

const GLYPH: Record<AgencyType, () => React.JSX.Element> = {
  LawEnforcement: ShieldGlyph,
  Prosecution: ScaleGlyph,
  Defense: BriefcaseGlyph,
  Court: LandmarkGlyph,
  ClerkOfCourt: StampGlyph,
};

export function AgencyTypeIcon({ type, label, className }: AgencyTypeIconProps) {
  const Glyph = GLYPH[type];

  return (
    <span role="img" aria-label={label} className={className}>
      <svg
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="shrink-0"
        style={{ color: token('bp-text-secondary') }}
      >
        <Glyph />
      </svg>
    </span>
  );
}
